import { API_ROUTES } from '@shared/api-utils';
import type { Currency } from '@shared/utils';
import type {
  DepositLimit,
  DepositLimitCreator,
  DepositLimitsResponse,
  LimitPeriod,
  PlayerLimit,
  PlayerLimitCreator,
  PlayerLimitMoney,
  PlayerLimitPeriod,
  PlayerLimitRequest,
} from '../types';

const API_PERIOD_BY_LIMIT_PERIOD: Record<LimitPeriod, PlayerLimitPeriod> = {
  DAY: 'DAILY',
  WEEK: 'WEEKLY',
  MONTH: 'MONTHLY',
};

const LIMIT_PERIOD_BY_API_PERIOD: Record<PlayerLimitPeriod, LimitPeriod> = {
  DAILY: 'DAY',
  WEEKLY: 'WEEK',
  MONTHLY: 'MONTH',
};

const DEPOSIT_LIMIT_TYPE = 'DEPOSIT';

function toMinorUnits(majorUnits: number | undefined): number | undefined {
  if (majorUnits === undefined) {
    return undefined;
  }

  return Math.round(Number(majorUnits) * 100);
}

function toMajorUnits(minorUnits: number): number {
  return Number((minorUnits / 100).toFixed(2));
}

function moneyToMinorUnits(money: PlayerLimitMoney | undefined): number | undefined {
  return toMinorUnits(money?.value);
}

function isEncodedPendingLimitRemoval(value: number | undefined): boolean {
  return !value || value < 0;
}

function toApiPeriod(period: LimitPeriod): PlayerLimitPeriod {
  return API_PERIOD_BY_LIMIT_PERIOD[period];
}

function toLimitPeriod(period: PlayerLimitPeriod): LimitPeriod {
  return LIMIT_PERIOD_BY_API_PERIOD[period];
}

function getDepositLimitsPath(): string {
  return API_ROUTES.MY_ACCOUNT.DEPOSIT_LIMITS.GET;
}

function getDepositLimitPath(period: LimitPeriod, limitType = DEPOSIT_LIMIT_TYPE): string {
  return API_ROUTES.MY_ACCOUNT.DEPOSIT_LIMITS.ADD.replace(':limitType', limitType).replace(':period', toApiPeriod(period));
}

function getPendingDepositLimitPath(period: LimitPeriod, limitType = DEPOSIT_LIMIT_TYPE): string {
  return API_ROUTES.MY_ACCOUNT.DEPOSIT_LIMITS.ACCEPT.replace(':limitType', limitType).replace(':period', toApiPeriod(period));
}

function createPlayerLimitRequest(amountInMajorUnits: number, currency: Currency): PlayerLimitRequest {
  return { value: amountInMajorUnits, currency };
}

function mapCreator(creator: PlayerLimitCreator | undefined): DepositLimitCreator | undefined {
  if (!creator) {
    return undefined;
  }

  return {
    admin: creator.admin,
    id: { timestamp: 0, date: '' },
    username: creator.username,
  };
}

function mapPlayerLimitToDepositLimit(limit: PlayerLimit): DepositLimit {
  const pendingLimit = limit.pendingLimit
    ? {
        validFrom: limit.pendingLimit.validFrom,
        value: moneyToMinorUnits(limit.pendingLimit.value) ?? 0,
        creationTime: limit.pendingLimit.creationTime,
        creator: mapCreator(limit.pendingLimit.creator) ?? { admin: false, id: { timestamp: 0, date: '' }, username: '' },
        confirmationNeeded: limit.pendingLimit.confirmationNeeded,
      }
    : undefined;

  return {
    limitType: limit.limitType,
    period: toLimitPeriod(limit.period),
    limitValue: moneyToMinorUnits(limit.limitValue),
    remaining: moneyToMinorUnits(limit.remaining),
    used: moneyToMinorUnits(limit.used),
    creationTime: limit.creationTime,
    startTime: limit.startTime,
    expirationTime: limit.expirationTime,
    creator: mapCreator(limit.creator),
    ...(pendingLimit ? { pendingLimit } : {}),
  };
}

function mapPlayerLimitsToDepositLimitsResponse(limits: PlayerLimit[] | null | undefined): DepositLimitsResponse | null {
  if (!limits) {
    return null;
  }

  return { limits: limits.map(mapPlayerLimitToDepositLimit) };
}

export {
  createPlayerLimitRequest,
  DEPOSIT_LIMIT_TYPE,
  getDepositLimitPath,
  getDepositLimitsPath,
  getPendingDepositLimitPath,
  isEncodedPendingLimitRemoval,
  mapPlayerLimitsToDepositLimitsResponse,
  mapPlayerLimitToDepositLimit,
  toApiPeriod,
  toLimitPeriod,
  toMajorUnits,
  toMinorUnits,
};
